import { LayoutDashboard, Phone, Layers, BarChart2, Settings, Bell, Shield, Radio, ShieldCheck, LogOut, Users, MapPin, Activity, PhoneCall, GitBranch } from 'lucide-react';

const NAV = [
  { section: 'Overview' },
  { id: 'dashboard', label: 'Dashboard',            icon: LayoutDashboard },
  { section: 'Analytics' },
  { id: 'analytics',   label: 'Analytics',          icon: BarChart2 },
  { id: 'callquality', label: 'Teams Call Quality', icon: Activity },
  { section: 'Users' },
  { id: 'users',     label: 'User Directory',        icon: Users },
  { id: 'callqueues',     label: 'Call Queues',        icon: PhoneCall },
  { id: 'autoattendants', label: 'Auto Attendants',   icon: GitBranch },
  { section: 'Numbers & Routing' },
  { id: 'numbers',   label: 'Number Inventory',      icon: Phone },
  { id: 'voxbone',   label: 'Number Routing',        icon: Radio },
  { id: 'bwrouting', label: 'BW Number Routing',     icon: Radio },
  { section: 'Infrastructure' },
  { id: 'platforms', label: 'UC / CC Platforms',     icon: Layers },
  { section: 'System' },
  { id: 'sites',     label: 'Sites',                 icon: MapPin },
  { id: 'alerts',    label: 'Alerts',                icon: Bell },
  { id: 'settings',  label: 'Settings',              icon: Settings },
  { id: 'security',  label: 'Security & Compliance', icon: Shield },
];

const ADMIN_NAV = [
  { section: 'Administration' },
  { id: 'admin', label: 'Admin Portal', icon: ShieldCheck },
];

// Keep only the modules this customer is allowed to see. enabledModules null
// means "all" (default). Section headers with no visible items are dropped.
function filterNav(nav, enabledModules) {
  if (!Array.isArray(enabledModules)) return nav;
  const allowed = new Set(enabledModules);
  const kept = nav.filter(item => item.section || allowed.has(item.id));
  // drop section headers immediately followed by another section or the end
  return kept.filter((item, i) => {
    if (!item.section) return true;
    const next = kept[i + 1];
    return next && !next.section;
  });
}

export default function Sidebar({ active, onNav, user, onLogout, customers = [], viewAsCustomer, onViewAsChange, alertCount = 0, mobileOpen = false, onMobileClose }) {
  const items           = user?.isAdmin ? [...NAV, ...ADMIN_NAV] : filterNav(NAV, user?.enabledModules);
  const initials        = (user?.name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const isImpersonating = Boolean(viewAsCustomer);

  function handleNav(id) {
    onNav(id);
    onMobileClose?.();
  }

  return (
    <aside className={`sidebar${mobileOpen ? ' mobile-open' : ''}`}>
      <div className="sidebar-logo">
        <div className="logo-mark">
          <div className="logo-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" fill="var(--on-brand)"/>
            </svg>
          </div>
          <div>
            <div className="logo-text">Damovo</div>
            <div className="logo-sub">Hosting Platform</div>
          </div>
        </div>

        {user?.isAdmin && customers.length > 0 && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <Users size={12} color="var(--muted)"/>
              <span style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 500 }}>View as customer</span>
            </div>
            <select
              value={viewAsCustomer?.id ?? ''}
              onChange={e => {
                const id = e.target.value;
                if (!id) { onViewAsChange(null); return; }
                const c = customers.find(x => String(x.id) === id);
                if (c) onViewAsChange({ id: c.id, name: c.name });
              }}
              style={{
                width: '100%',
                background: isImpersonating ? 'var(--info-bg)' : 'var(--bg)',
                border: `1px solid ${isImpersonating ? '#2563eb66' : 'var(--border)'}`,
                borderRadius: 6,
                color: isImpersonating ? 'var(--info)' : 'var(--muted-2)',
                fontSize: 11,
                padding: '5px 8px',
                cursor: 'pointer',
                outline: 'none',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23475569'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 8px center',
                paddingRight: 24,
              }}
            >
              <option value="">— All customers —</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {isImpersonating && (
              <div style={{ fontSize: 10, color: 'var(--info)', marginTop: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--info)', flexShrink: 0, display: 'inline-block' }}/>
                Viewing: {viewAsCustomer.name}
              </div>
            )}
          </div>
        )}
      </div>

      <nav className="sidebar-nav">
        {items.map((item, i) => {
          if (item.section) {
            return <div key={i} className="nav-section">{item.section}</div>;
          }
          const Icon = item.icon;
          return (
            <div
              key={item.id}
              className={`nav-item ${active === item.id ? 'active' : ''}`}
              onClick={() => handleNav(item.id)}
              style={{ position: 'relative' }}
            >
              <Icon size={16} />
              {item.label}
              {item.id === 'alerts' && alertCount > 0 && (
                <span style={{
                  marginLeft: 'auto', background: 'var(--err)', color: 'var(--on-brand)',
                  fontSize: '10px', fontWeight: 700, borderRadius: '10px',
                  padding: '1px 6px',
                }}>{alertCount}</span>
              )}
            </div>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <div className="status-pill">
          <span className="dot green" />
          <span>All systems operational</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, padding: '6px 4px' }}>
          <div className="avatar">{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.name || 'User'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted-3)' }}>
              {user?.isAdmin ? 'Admin' : 'Customer'}
            </div>
          </div>
          {onLogout && (
            <button
              onClick={onLogout}
              title="Sign out"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted-3)', padding: 4, borderRadius: 4, display: 'flex', alignItems: 'center' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--err)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--muted-3)'}
            >
              <LogOut size={14} />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
